'use client';

import React, { useState, useEffect } from 'react';
import { ethers } from 'ethers';
import { usePrivy } from '@privy-io/react-auth';
import { useAtomValue, useAtom } from 'jotai';
import { truncateAddress64 } from '@/app/helpers/truncateAddress';
import { crank } from '@/app/helpers/crank';
import { hasTokensAtom, tokenInfoAtom } from '@/app/atoms/token';
import { userDataAtom } from '@/app/atoms/userData';
import { userInfoAtom } from '@/app/atoms/users';
import BaseModal from '@/app/components/Modals/BaseModal';
import ActionButton from './components/ActionButton';
import ModalConnect from '@/app/components/Modals/components/ModalConnect';
import { useTokenContext } from '@/app/context/TokensContext';
import { UniversalAvatar } from '../UniversalAvatar';
import { UsersAccountReturnType } from '@/app/types/account';
import { openTransactionUrl } from '@/app/helpers/openTransactionUrl';

interface CardDelegationModalProps {
  isOpen: boolean;
  onClose: () => void;
  delegateAddress: `0x${string}`;
  action: 'delegate' | 'revoke';
  activeModalDelegate: `0x${string}` | null;
  setActiveModalDelegate: (delegate: `0x${string}` | null) => void;
}

const CardDelegationModal: React.FC<CardDelegationModalProps> = ({
  isOpen,
  onClose,
  delegateAddress,
  action,
  activeModalDelegate,
  setActiveModalDelegate,
}) => {
  const { user, authenticated } = usePrivy();
  const isConnected = authenticated && !!user;
  const address = user?.wallet?.address as `0x${string}` | undefined;
  const tokenInfo = useAtomValue(tokenInfoAtom);
  const { balance, symbol } = tokenInfo;
  const { tokenDelegate } = useTokenContext();
  const [isPending, setIsPending] = useState(false);
  const [isConfirming, setIsConfirming] = useState(false);
  const [isConfirmed, setIsConfirmed] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [txHash, setTxHash] = useState<string | null>(null);
  const [settings, setSettings] = useState<UsersAccountReturnType | undefined>();
  const hasTokens = useAtomValue(hasTokensAtom);
  const userData = useAtomValue(userDataAtom);
  const [users] = useAtom(userInfoAtom);
  const userHasDelegate = userData.delegateCount > 0;
  const usersDelegate = userHasDelegate && userData.delegatesAddress[0];
  const isProcessing = isPending || isConfirming;
  const userIsSelfDelegated = usersDelegate === userData.address;

  const handleDelegation = async () => {
    setActiveModalDelegate(delegateAddress as `0x${string}`);
    setIsPending(true);
    setIsConfirming(false);
    setIsConfirmed(false);
    setError(null);
    try {
      const result = await tokenDelegate({
        delegatee:
          action === 'revoke'
            ? (ethers.ZeroAddress as `0x${string}`)
            : (delegateAddress as `0x${string}`),
      });
      setTxHash(result.txHash);
      setIsConfirming(true);
      // Triggering the API data update
      crank();
      setIsConfirmed(true);
    } catch (err) {
      setError('An error occurred during delegation');
    } finally {
      setIsPending(false);
      setIsConfirming(false);
    }
  };

  const handleViewTx = () => {
    if (txHash) {
      openTransactionUrl(txHash);
    }
  };

  const getButtonText = () => {
    if (isPending) return action === 'revoke' ? 'Revoking...' : 'Delegating...';
    if (isConfirming) return 'Processing...';
    if (isConfirmed && txHash) return 'View on BlockExplorer';
    if (isConfirmed) return action === 'revoke' ? 'Revoked' : 'Delegated';
    if (error) return 'Error';
    return action === 'revoke' ? 'Revoke' : 'Delegate';
  };

  let title = action === 'revoke' ? 'Revoke' : 'Delegate';

  if (!isConnected) {
    title = 'Connect Wallet';
  } else if (!hasTokens) {
    title = `No ${symbol}'s found`;
  }

  useEffect(() => {
    if (error) {
      onClose();
    }
  }, [error, onClose]);

  useEffect(() => {
    const newSettings = users.find(
      (user: UsersAccountReturnType) => user.wallet_address === address,
    );
    setSettings(newSettings);
  }, [address, users]);

  return (
    <BaseModal
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      disableClose={isProcessing}
    >
      {!isConnected ? (
        <ModalConnect />
      ) : !hasTokens ? (
        <div className="text-center">
          <p className="mt-4 mb-6 text-pretty">
            {symbol} ownership is what determines voting and delegation power. Once you
            have {symbol} tokens, you can then delegate them to a wallet address to gain
            voting rights.
          </p>
        </div>
      ) : (
        <div className="flex flex-col pt-4 items-center min-w-full">
          <p>
            {action === 'revoke'
              ? 'Revoking voting rights from'
              : isConfirmed
                ? `Delegated ${balance} ${symbol}s to`
                : `Delegating ${balance} ${symbol}s to`}
          </p>
          <div className="flex items-center mb-4 mt-2">
            <UniversalAvatar
              address={(activeModalDelegate as `0x${string}`) ?? delegateAddress}
              width={20}
              height={20}
              size={20}
            />
          </div>
          <div className="flex flex-col mt-4 py-2 w-full">
            {userHasDelegate && !isConfirming && !isConfirmed && action !== 'revoke' && (
              <div className="flex flex-wrap italic text-pretty text-neutral-50">
                This will override your current delegation to{' '}
                {truncateAddress64(usersDelegate as string)}
              </div>
            )}
            {action === 'revoke' && !!userIsSelfDelegated && (
              <div className="flex flex-wrap italic text-pretty">
                By revoking your voting rights, you will not be able to vote on future
                proposals.
              </div>
            )}
            <ActionButton
              selectedAction={action}
              isPending={isPending}
              isConfirming={isConfirming}
              buttonTxt={getButtonText()}
              handleSubmit={() => {}}
              handleButtonClick={handleDelegation}
              handleViewTx={handleViewTx}
              userData={userData}
            />
          </div>
        </div>
      )}
    </BaseModal>
  );
};

export default CardDelegationModal;
